import { action } from '@reatom/framework'
import i18next from 'i18next'

import { Result, Array } from '@blade/std-lib'
import { AuthState, authStateAtom, User, userAtom } from '@root/entities/auth'
import * as InstantTradeBuyButton from '@root/entities/instant-trade-buy-button/instant-trade-buy-button'
import * as InstantTradeSellButton from '@root/entities/instant-trade-sell-button/instant-trade-sell-button'
import { Error } from '@root/shared'
import { apiRequest } from '@root/shared/api'
import { tradeApiClientAtom } from '@root/shared/api/trade-api-client-atom'
import { reatomNumericInput, reatomRequest } from '@root/shared/lib'
import { showSnackbarAction } from '@root/shared/ui-kit'

import type { ReatomNumericInput } from '@root/shared/lib'
import type BigNumber from 'bignumber.js'

interface EditInstantTradeButtonsPayload {
  buyButtons: Array<InstantTradeBuyButton.InstantTradeBuyButton> | undefined
  sellButtons: Array<InstantTradeSellButton.InstantTradeSellButton> | undefined
}

export const editInstantTradeButtonsRequestAction = action(
  async (ctx, payload: EditInstantTradeButtonsPayload) => {
    const { tradeApiClient } = ctx.get(tradeApiClientAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.user.userEditInstantTradeButtonsCreate, [
        {
          ...payload,
          sellButtons: payload.sellButtons?.map((x) => ({
            key: x.key,
            percent: x.percent.toNumber(),
          })),
          buyButtons: payload.buyButtons?.map((x) => ({
            key: x.key,
            amount: x.amount.toNumber(),
          })),
        },
      ]),
    )

    return Result.map(result, (dto) => ({
      buyButtons: dto.buyButtons.map((x) => InstantTradeBuyButton.fromDto(x)),
      sellButtons: dto.sellButtons.map((x) =>
        InstantTradeSellButton.fromDto(x),
      ),
    }))
  },
  'editInstantTradeButtonsRequestAction',
)

export const reatomInstantTradeButtonsForm = (args: {
  buyButtons: readonly [BigNumber, BigNumber, BigNumber, BigNumber] | undefined
  sellButtons: readonly [BigNumber, BigNumber, BigNumber] | undefined
}) => {
  const buyInputs = args.buyButtons?.map((x) =>
    // eslint-disable-next-line @reatom/unit-naming-rule
    reatomNumericInput({ initialAmount: x }),
  ) as
    | undefined
    | [
        ReatomNumericInput,
        ReatomNumericInput,
        ReatomNumericInput,
        ReatomNumericInput,
      ]

  const sellInputs = args.sellButtons?.map((x) =>
    // eslint-disable-next-line @reatom/unit-naming-rule
    reatomNumericInput({ initialAmount: x }),
  ) as undefined | [ReatomNumericInput, ReatomNumericInput, ReatomNumericInput]

  const processUpdateAction = action(async (ctx) => {
    const user = ctx.get(userAtom)
    const buyButtons = buyInputs?.map((x, i) => ({
      key: `buy_${(i + 1).toString()}`, // TODO
      amount: ctx.get(x),
    }))
    const sellButtons = sellInputs?.map((x, i) => ({
      key: `sell_${(i + 1).toString()}`, // TODO
      percent: ctx.get(x),
    }))
    if (!user || (!buyButtons && !sellButtons)) {
      return Result.fail(Error.UnknownError.fromUnknown('Invalid input'))
    }

    if (
      buyButtons &&
      User.eqBuyButtonArray(user.instantTradeButtons.buyButtons, buyButtons)
    ) {
      return Result.succeed(undefined)
    }

    if (sellButtons) {
      const currentSellButtons = Array.take(
        user.instantTradeButtons.sellButtons,
        sellButtons.length,
      )

      if (User.eqSellButtonArray(currentSellButtons, sellButtons)) {
        return Result.succeed(undefined)
      }
    }

    const result = await ctx.schedule(() =>
      editInstantTradeButtonsRequestAction(ctx, { buyButtons, sellButtons }),
    )

    if (Result.isFailure(result)) {
      showSnackbarAction(ctx, {
        text: i18next.t('common.error'),
        description: i18next.t('common.something-went-wrong'),
        intent: 'error',
      })
      return result
    }

    const authState = ctx.get(authStateAtom)
    if (AuthState.isLoggedIn(authState)) {
      authState.user(ctx, (user) => ({
        ...user,
        instantTradeButtons: result.success,
      }))
    }

    showSnackbarAction(ctx, {
      text: i18next.t('instant-trade.swap-buttons.saved'),
      intent: 'success',
    })
    return result
  }, 'processUpdateAction')
  const instantSellButtonsFormState = reatomRequest({
    execAction: processUpdateAction,
    // eslint-disable-next-line @reatom/unit-naming-rule
    name: 'instantSellButtonsFormState',
  })

  return { instantSellButtonsFormState, buyInputs, sellInputs }
}
